import React, { useMemo } from 'react';

import { axisScale, referenceMarks, scaleBounds } from '../axisStyle';
import { formatAxis, formatValue, memberLabel, unitName } from '../format';
import type { Palette } from '../palette';
import type { Block, Envelope, EnvelopeColumn } from '../types';

import Chart, { type ChartOption } from './Chart';

type BoxPlotProps = { block: Block; envelope: Envelope; palette: Palette; selected: Array<string>; onPick?: (member: string) => void };

const STATS = ['min', 'p25', 'median', 'p75', 'max'] as const;

const statOf = (column: EnvelopeColumn) => column.name.slice(0, column.name.indexOf('@'));

export function boxOption({ block, envelope, palette, selected }: Omit<BoxPlotProps, 'onPick'>): ChartOption | undefined {
  const dim = envelope.columns.find((column) => column.role === 'dim');
  const measures = envelope.columns.filter((column) => column.role === 'measure');
  const columns = STATS.map((stat) => measures.find((column) => statOf(column) === stat));
  if (columns.some((column) => !column)) return undefined;
  const format = columns[2]!.format;
  const rows = envelope.rows.filter((row) => !row.other && (row.level === undefined || row.level === 1));
  const members = dim ? rows.map((row) => String(row[dim.name])) : [block.title ? String(block.title) : 'All'];
  const values = (dim ? rows : [envelope.totals?.current ?? rows[0] ?? {}]).map((row) => columns.map((column) => (row?.[column!.name] as number | null) ?? null));
  const colour = palette.series[0];
  return {
    animation: false,
    textStyle: { fontFamily: palette.sans, fontSize: 11, color: palette.ink3 },
    grid: { left: 8, right: 16, top: 12, bottom: typeof block.yTitle === 'string' && block.yTitle ? 30 : 8, containLabel: true },
    tooltip: {
      trigger: 'item',
      backgroundColor: palette.surface,
      borderColor: palette.hairlineStrong,
      textStyle: { color: palette.ink, fontSize: 12, fontFamily: palette.sans },
      confine: true,
      formatter: (item: { dataIndex: number }) =>
        [memberLabel(members[item.dataIndex]), ...STATS.map((_, index) => `${columns[index]!.label}: ${formatValue(values[item.dataIndex][index], format)}`).reverse()].join('<br/>'),
    },
    yAxis: {
      type: 'category',
      inverse: true,
      triggerEvent: true,
      data: members,
      axisTick: { show: false },
      axisLine: { lineStyle: { color: palette.hairlineStrong } },
      axisLabel: { color: palette.ink2, fontSize: 11, formatter: memberLabel },
    },
    xAxis: {
      type: 'value',
      triggerEvent: true,
      scale: !axisScale(block).zero,
      ...scaleBounds(axisScale(block)),
      splitNumber: 4,
      ...(typeof block.yTitle === 'string' && block.yTitle ? { name: block.yTitle, nameLocation: 'middle', nameGap: 26, nameTextStyle: { color: palette.ink3, fontSize: 11 } } : unitName(format, palette.ink3)),
      axisLabel: { color: palette.ink3, fontSize: 11, formatter: (value: number) => formatAxis(value, format) },
      splitLine: { lineStyle: { color: palette.hairline } },
    },
    series: [
      {
        id: 'spread',
        type: 'boxplot',
        boxWidth: [6, 22],
        data: values.map((value, index) => ({
          value,
          itemStyle: { color: palette.surface, borderColor: colour, borderWidth: 1.5, opacity: selected.length && !selected.includes(members[index]) ? 0.35 : 1 },
        })),
        ...referenceMarks(block, palette, format),
      },
    ],
  };
}

export default function BoxPlot({ block, envelope, palette, selected, onPick }: BoxPlotProps) {
  const option = useMemo(() => boxOption({ block, envelope, palette, selected }), [block, envelope, palette, selected]);
  if (!option) return <p className="bn">This box plot’s figures didn’t come back.</p>;
  return <Chart onItem={onPick} option={option} />;
}
